import { useEffect, useRef, useState } from "react";
import { CanonicalLink as Link } from "@/components/CanonicalLink";

declare global {
  interface Window {
    getAdvertisingConsent?: () => boolean | null;
    setAdvertisingConsent?: (accept: boolean) => void;
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState("");
  const firstButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setVisible(window.getAdvertisingConsent?.() == null);
    const open = () => {
      opener.current = document.activeElement as HTMLElement | null;
      setVisible(true);
      requestAnimationFrame(() => firstButton.current?.focus());
    };
    window.addEventListener("plomberie:cookie-preferences", open);
    return () => window.removeEventListener("plomberie:cookie-preferences", open);
  }, []);

  function choose(accept: boolean) {
    if (!window.setAdvertisingConsent) {
      setError("Les préférences n’ont pas pu être enregistrées. Rechargez la page pour réessayer.");
      return;
    }
    window.setAdvertisingConsent(accept);
    setVisible(false);
    setError("");
    opener.current?.focus();
  }

  if (!visible) return null;
  return (
    <section
      role="region"
      aria-labelledby="cookie-choice-heading"
      data-testid="cookie-consent"
      className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-5 text-primary shadow-xl sm:inset-x-auto sm:left-5 sm:mx-0"
    >
      <h2 id="cookie-choice-heading" className="text-base font-bold">Votre choix de cookies</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Avec votre accord, Google Ads utilise des cookies pour mesurer nos publicités.
        Vous pouvez refuser : les formulaires et le site restent disponibles.
      </p>
      <Link href="/politique-de-confidentialite/" className="mt-2 inline-block text-sm underline underline-offset-4">
        Politique de confidentialité
      </Link>
      {error && <p role="alert" className="mt-2 text-sm text-red-800">{error}</p>}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button ref={firstButton} type="button" onClick={() => choose(false)} className="min-h-11 rounded-md border border-primary px-3 py-2 text-sm font-semibold hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2">
          Refuser
        </button>
        <button type="button" onClick={() => choose(true)} className="min-h-11 rounded-md border border-primary bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
          Accepter
        </button>
      </div>
    </section>
  );
}